import { loadRemoteModule } from '@angular-architects/module-federation';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { enableProdMode, StaticProvider, ValueProvider } from '@angular/core';
import { initializeGlobals } from 'src/app/globals/globals';
import { LoadedMfeHelper } from 'src/app/services/loaded-mfe.helper';
import { APP_ENVIRONMENT, AppEnvironment } from '../app/app.environment';
import { AppModule } from '../app/app.module';
import { bootstrapData } from './bootstrap-data';
import { RootProvidersModuleContainer } from './RootProvidersModule';

// TODO: Add a logic that evaluates the value of the environment configurations
// Explore schema validation.
// Overall goal is fail fast!
async function fetchShellEnvironmentJson(): Promise<AppEnvironment | never> {
  try {
    const response = await fetch('/configs/environment.json');
    return response.json();
  } catch (error) {
    console.error('Failed to load environment configuration:', error);
    throw error;
  }
}

async function loadRootProviderModules(
  environment: AppEnvironment
): Promise<RootProvidersModuleContainer[]> {
  const promises: Promise<RootProvidersModuleContainer | undefined>[] =
    Object.values(environment.apps).map(async (app) => {
      // The app.path !== 'analytics' is just a precaution against cached environment jsons
      if (
        !app.hostname ||
        (!app.hasRootProviders && app.path !== 'analytics')
      ) {
        return Promise.resolve(undefined);
      }

      try {
        const container = await loadRemoteModule<RootProvidersModuleContainer>({
          type: 'module',
          remoteEntry: `${app.hostname}/remoteEntry.js`,
          exposedModule: './RootProvidersModule'
        });

        return container;
      } catch (err) {
        console.warn('Failed to load remote module:', err);
        bootstrapData.mfeStates[app.path] = 'error-loading-module';
        return undefined;
      }
    });

  const containers = await Promise.all(promises);
  return containers.filter((x): x is RootProvidersModuleContainer => !!x);
}

/**
 * Fetches the environment.json files for all the MFEs and then returns an array of providers for those environment jsons
 */
async function loadMFEEnvironmentProviders(
  environment: AppEnvironment
): Promise<ValueProvider[]> {
  const promises: Promise<ValueProvider | undefined>[] = Object.values(
    environment.apps
  )
    .filter(
      (x) =>
        !!x?.path && bootstrapData.mfeStates[x.path] !== 'error-loading-module'
    )
    .map(async (app: any) => {
      try {
        // Injection token should be a string so that it can be easily accessed by the MFEs
        const provide = `${app.path.toLocaleUpperCase()}_APP_ENVIRONMENT`;
        const response = await fetch(
          `${app.hostname}/configs/environment.json`
        );
        if (!response.ok) {
          throw new Error('Failed to fetch config');
        }
        const useValue = await response.json();
        return { provide, useValue };
      } catch (err) {
        bootstrapData.mfeStates[app.path] = 'error-loading-module';
        console.warn(
          `Failed to load environment configuration for ${app.path}:`,
          err
        );
        return undefined;
      }
    });

  const providers = await Promise.all(promises);
  return providers.filter(
    (x): x is ValueProvider => !!x && x.provide && x.useValue
  );
}

function performActualBootstrapping(
  environment: any,
  rootProviders: StaticProvider[],
  mfeEnvironmentProviders: ValueProvider[]
) {
  if (environment === undefined || environment?.production !== false) {
    enableProdMode();
  }

  const shellProviders = [
    {
      provide: APP_ENVIRONMENT,
      useValue: environment
    }
  ];

  platformBrowserDynamic([
    ...rootProviders,
    ...shellProviders,
    ...mfeEnvironmentProviders
  ])
    .bootstrapModule(AppModule)
    .catch((err) => console.error('Failed to bootstrap the app:', err));
}

async function bootstrapShell() {
  const environmentJson = await fetchShellEnvironmentJson();
  const rootProvidersContainers =
    await loadRootProviderModules(environmentJson);
  const mfeEnvironmentProviders =
    await loadMFEEnvironmentProviders(environmentJson);

  LoadedMfeHelper.setLoadedAppFromCurrentLocation();

  const rootProviders = rootProvidersContainers
    .flatMap((x) => x?.RootProvidersModule?.getRootProviders?.() ?? [])
    .filter((x) => !!x);

  performActualBootstrapping(
    environmentJson,
    rootProviders,
    mfeEnvironmentProviders
  );
}

initializeGlobals();
navigator.serviceWorker?.register('user-sw.js');
bootstrapShell();
